import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useMemo } from "react";
import { VectorMap } from "react-jvectormap";

import "../../../../../vendor/jvectormap.css";

const STATE_COORDINATES = {
  AL: [32.8, -86.8],
  AK: [61.2, -149.9],
  AZ: [33.4, -112.1],
  AR: [34.7, -92.3],
  CA: [36.8, -119.4],
  CO: [39.6, -105.0],
  CT: [41.6, -72.7],
  DE: [39.2, -75.5],
  DC: [38.9, -77.0],
  FL: [28.6, -82.4],
  GA: [33.2, -83.4],
  HI: [21.3, -157.9],
  ID: [43.6, -114.7],
  IL: [40.0, -89.4],
  IN: [39.8, -86.2],
  IA: [42.0, -93.5],
  KS: [38.5, -98.3],
  KY: [37.8, -85.8],
  LA: [30.5, -91.9],
  ME: [44.7, -69.4],
  MD: [39.0, -76.8],
  MA: [42.2, -71.5],
  MI: [43.3, -84.5],
  MN: [45.7, -93.9],
  MS: [32.7, -89.7],
  MO: [38.6, -92.6],
  MT: [46.9, -110.4],
  NE: [41.5, -99.9],
  NV: [38.8, -116.4],
  NH: [43.2, -71.6],
  NJ: [40.1, -74.7],
  NM: [34.5, -106.0],
  NY: [42.2, -74.9],
  NC: [35.6, -79.8],
  ND: [47.5, -100.5],
  OH: [40.4, -82.8],
  OK: [35.5, -97.5],
  OR: [44.6, -122.1],
  PA: [40.6, -77.2],
  PR: [18.2, -66.3],
  RI: [41.7, -71.5],
  SC: [34.0, -81.0],
  SD: [44.4, -100.2],
  TN: [35.9, -86.4],
  TX: [31.5, -97.6],
  UT: [40.2, -111.7],
  VT: [44.1, -72.6],
  VA: [37.5, -78.9],
  VI: [18.3, -64.9],
  WA: [47.4, -120.5],
  WV: [38.6, -80.6],
  WI: [44.3, -89.6],
  WY: [43.1, -107.6],
};

const ZIP_RANGES = [
  [5, 5, "NY"],
  [6, 9, "PR"],
  [10, 27, "MA"],
  [28, 29, "RI"],
  [30, 38, "NH"],
  [39, 49, "ME"],
  [50, 59, "VT"],
  [60, 69, "CT"],
  [70, 89, "NJ"],
  [100, 149, "NY"],
  [150, 196, "PA"],
  [197, 199, "DE"],
  [200, 205, "DC"],
  [206, 219, "MD"],
  [220, 246, "VA"],
  [247, 268, "WV"],
  [270, 289, "NC"],
  [290, 299, "SC"],
  [300, 319, "GA"],
  [320, 349, "FL"],
  [350, 369, "AL"],
  [370, 384, "TN"],
  [385, 397, "MS"],
  [400, 427, "KY"],
  [430, 458, "OH"],
  [460, 479, "IN"],
  [480, 499, "MI"],
  [500, 528, "IA"],
  [530, 549, "WI"],
  [550, 567, "MN"],
  [570, 577, "SD"],
  [580, 588, "ND"],
  [590, 599, "MT"],
  [600, 629, "IL"],
  [630, 658, "MO"],
  [660, 679, "KS"],
  [680, 693, "NE"],
  [700, 714, "LA"],
  [716, 729, "AR"],
  [730, 749, "OK"],
  [750, 799, "TX"],
  [800, 816, "CO"],
  [820, 831, "WY"],
  [832, 838, "ID"],
  [840, 847, "UT"],
  [850, 865, "AZ"],
  [870, 884, "NM"],
  [889, 898, "NV"],
  [900, 961, "CA"],
  [967, 968, "HI"],
  [970, 979, "OR"],
  [980, 994, "WA"],
  [995, 999, "AK"],
];

const getStateForZip = (zipcode) => {
  const prefix = parseInt(String(zipcode).padStart(5, "0").substring(0, 3), 10);
  for (const [min, max, state] of ZIP_RANGES) {
    if (prefix >= min && prefix <= max) return state;
  }
  return "";
};

const getCoordinatesForZip = (zipcode) => {
  const state = getStateForZip(zipcode);
  return STATE_COORDINATES[state] || null;
};

const getDeliveryColor = (days) => {
  if (days == null) return "#94a3b8";
  if (days <= 2) return "#16a34a";
  if (days <= 3) return "#65a30d";
  if (days <= 4) return "#ca8a04";
  if (days <= 5) return "#ea580c";
  return "#dc2626";
};

const getDeliveryBgColor = (days) => {
  if (days == null) return "#f1f5f9";
  if (days <= 2) return "#dcfce7";
  if (days <= 3) return "#ecfccb";
  if (days <= 4) return "#fef9c3";
  if (days <= 5) return "#ffedd5";
  return "#fee2e2";
};

const getDeliveryLabel = (days) => {
  if (days == null) return "N/A";
  if (days <= 2) return "Fast";
  if (days <= 3) return "Good";
  if (days <= 4) return "Average";
  if (days <= 5) return "Slow";
  return "Very Slow";
};

const MapContainer = styled(Box)({
  height: "260px",
  width: "100%",
  position: "relative",
  borderRadius: "8px",
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  marginBottom: "16px",
});

const LegendContainer = styled(Box)({
  display: "flex",
  gap: "12px",
  justifyContent: "center",
  padding: "8px 0",
  flexWrap: "wrap",
});

const LegendItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "11px",
  color: "#64748b",
});

const LegendDot = styled(Box)(({ color }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: color,
}));

const StyledTableCell = styled(TableCell)({
  fontSize: "12px",
  padding: "6px 12px",
  borderBottom: "1px solid #f1f5f9",
});

const StyledHeaderCell = styled(TableCell)({
  fontSize: "11px",
  fontWeight: 600,
  padding: "8px 12px",
  backgroundColor: "#f8fafc",
  color: "#475569",
  borderBottom: "2px solid #e2e8f0",
  whiteSpace: "nowrap",
});

const DaysBadge = styled(Box)(({ bgcolor, textcolor }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "2px 8px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  backgroundColor: bgcolor,
  color: textcolor,
}));

const calcDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;
  return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
};

const DeliveryTimesModal = ({
  open,
  onClose,
  store,
  childAsin,
  deliveryTimesData,
}) => {
  const data = useMemo(() => {
    if (!deliveryTimesData || !deliveryTimesData.length || !childAsin)
      return [];

    const asinTimes = deliveryTimesData.filter(
      (item) => item.child_asin === childAsin
    );

    const latestDate = asinTimes.reduce((max, item) => {
      if (!item.last_updated_date) return max;
      return !max || item.last_updated_date > max
        ? item.last_updated_date
        : max;
    }, null);

    return asinTimes
      .filter((item) => item.last_updated_date === latestDate)
      .map((item) => {
        const sampleDate = item.sample_date || item.last_updated_date;
        return {
          zipcode: item.zipcodes,
          prime_min: calcDaysBetween(sampleDate, item.prime_start_date),
          prime_max: calcDaysBetween(sampleDate, item.prime_end_date),
          delivery_min: calcDaysBetween(sampleDate, item.delivery_start_date),
          delivery_max: calcDaysBetween(sampleDate, item.delivery_end_date),
          last_updated_date: item.last_updated_date,
        };
      })
      .sort((a, b) => String(a.zipcode).localeCompare(String(b.zipcode)));
  }, [deliveryTimesData, childAsin]);

  const formatRange = (min, max) => {
    if (min == null || max == null) return "N/A";
    return min === max ? `${min}d` : `${min}-${max}d`;
  };

  const markers = data
    .map((item) => {
      const coords = getCoordinatesForZip(item.zipcode);
      if (!coords || item.prime_max == null) return null;
      return {
        latLng: coords,
        name: `${item.zipcode}: ${formatRange(
          item.prime_min,
          item.prime_max
        )} Prime / ${formatRange(
          item.delivery_min,
          item.delivery_max
        )} Standard`,
        style: {
          fill: getDeliveryColor(item.prime_max),
          r: 7,
        },
      };
    })
    .filter(Boolean);

  const mapOptions = {
    map: "us_aea",
    regionStyle: {
      initial: {
        fill: "#e3eaef",
        "fill-opacity": 1,
        stroke: "#cbd5e1",
        "stroke-width": 0.5,
      },
    },
    backgroundColor: "#f8fafc",
    containerStyle: {
      width: "100%",
      height: "100%",
    },
    markerStyle: {
      initial: {
        r: 7,
        fill: "#3b82f6",
        "fill-opacity": 0.9,
        stroke: "#fff",
        "stroke-width": 2,
        "stroke-opacity": 0.8,
      },
      hover: {
        stroke: "#fff",
        "fill-opacity": 1,
        "stroke-width": 2,
        r: 9,
      },
    },
    zoomOnScroll: false,
    markers,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <LocalShippingIcon sx={{ color: "#3b82f6", fontSize: "22px" }} />
          <Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: "16px", color: "#1e293b" }}
            >
              Delivery Times
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
              {childAsin} &middot; {store}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "20px 24px" }}>
        {data.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "40px",
              color: "#64748b",
              fontSize: "14px",
            }}
          >
            No delivery time data available for this product
          </Box>
        ) : (
          <>
            <MapContainer>
              <VectorMap {...mapOptions} />
            </MapContainer>

            <LegendContainer>
              <LegendItem>
                <LegendDot color="#16a34a" />
                1-2 days
              </LegendItem>
              <LegendItem>
                <LegendDot color="#65a30d" />3 days
              </LegendItem>
              <LegendItem>
                <LegendDot color="#ca8a04" />4 days
              </LegendItem>
              <LegendItem>
                <LegendDot color="#ea580c" />5 days
              </LegendItem>
              <LegendItem>
                <LegendDot color="#dc2626" />
                6+ days
              </LegendItem>
            </LegendContainer>

            <TableContainer
              sx={{
                maxHeight: "340px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                marginTop: "12px",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledHeaderCell>Zip Code</StyledHeaderCell>
                    <StyledHeaderCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: "12px" }} />
                        Prime Delivery
                      </Box>
                    </StyledHeaderCell>
                    <StyledHeaderCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <LocalShippingIcon sx={{ fontSize: "12px" }} />
                        Standard Delivery
                      </Box>
                    </StyledHeaderCell>
                    <StyledHeaderCell align="center">
                      Last Updated
                    </StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item) => (
                    <TableRow
                      key={item.zipcode}
                      sx={{
                        "&:hover": { backgroundColor: "#f8fafc" },
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: "4px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              color: "#1e293b",
                            }}
                          >
                            {item.zipcode}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#94a3b8",
                              fontWeight: 500,
                            }}
                          >
                            {getStateForZip(item.zipcode)}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <DaysBadge
                          bgcolor={getDeliveryBgColor(item.prime_max)}
                          textcolor={getDeliveryColor(item.prime_max)}
                        >
                          {formatRange(item.prime_min, item.prime_max)}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "9px",
                              opacity: 0.8,
                              fontWeight: 400,
                            }}
                          >
                            ({getDeliveryLabel(item.prime_max)})
                          </Typography>
                        </DaysBadge>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <DaysBadge
                          bgcolor={getDeliveryBgColor(item.delivery_max)}
                          textcolor={getDeliveryColor(item.delivery_max)}
                        >
                          {formatRange(item.delivery_min, item.delivery_max)}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "9px",
                              opacity: 0.8,
                              fontWeight: 400,
                            }}
                          >
                            ({getDeliveryLabel(item.delivery_max)})
                          </Typography>
                        </DaysBadge>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          {item.last_updated_date}
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                marginTop: "12px",
                padding: "8px 12px",
                backgroundColor: "#f0f9ff",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#0369a1" }}>
                Showing delivery estimates for {data.length} zip codes. Days
                calculated from order date to estimated delivery.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { DeliveryTimesModal };
